import { siteConfig } from "../config/siteConfig";
import { PageHero } from "./PageHero";
import { Separator } from "./ui/separator";

interface PrivacyPolicyPageProps {
  onNavigate: (page: string) => void;
}

export function PrivacyPolicyPage({ onNavigate }: PrivacyPolicyPageProps) {
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title="Privacy Policy"
        lede={<><p>TurfAI ("we", "our", "us") operates https://turfai.in and provides AI document processing and workflow automation services. This Privacy Policy explains how we collect, access, use, store, and protect data, including data accessed via Google OAuth services.</p><p className="mt-3 text-[15px] font-semibold text-soft-black">Effective Date: 21 January 2026</p></>}
      />


      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">1. Google API disclosure (Limited Use)</h2>
              <p className="text-soft-black">
                TurfAI's use and transfer to any other app of information received from Google APIs will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy" className="prose-link">Google API Services User Data Policy</a>, including the <strong>Limited Use requirements</strong>.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">2. Google OAuth data access and use</h2>
              <p className="text-soft-black mb-6">
                We only access Google user data after receiving your explicit consent and only to perform workflows you initiate.
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="text-[22px] font-semibold text-dark-indigo mb-3">Gmail data use (gmail.readonly, gmail.send)</h3>
                  <ul className="space-y-3 text-soft-black">
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                      <span>We read only specific emails explicitly selected or processed by the user.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                      <span>We send emails only when triggered by user actions (e.g., document delivery or workflow notifications).</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                      <span><strong>Prohibition:</strong> We do not continuously scan inboxes, access unrelated emails, or use Gmail data for advertising, profiling, or model training.</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-[22px] font-semibold text-dark-indigo mb-3">Google Drive data use (drive.file, drive.readonly)</h3>
                  <ul className="space-y-3 text-soft-black">
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                      <span>We access only the files you explicitly select, upload, or generate within TurfAI.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                      <span>We do not access unrelated Drive files or your full Drive contents.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">3. Data processing and AI limitations</h2>
              <ul className="space-y-3 text-soft-black">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>No Model Training:</strong> We do not use Google user data to train, retrain, or improve generalized AI/Machine Learning models.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>No Advertising:</strong> Your data is never used for advertising purposes.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <span><strong>Limited Human Review:</strong> Our employees do not access your Google user data unless:</span>
                    <ol className="list-decimal list-inside mt-2 ml-4 space-y-1">
                      <li>You provide explicit consent for troubleshooting a specific issue.</li>
                      <li>It is strictly necessary for security purposes (e.g., investigating abuse).</li>
                      <li>The data is aggregated and anonymized for internal operations.</li>
                    </ol>
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">4. Third-party data sharing</h2>
              <p className="text-soft-black">
                TurfAI does not sell your data. We only share Google user data with third-party service providers (such as cloud hosting or AI processing infrastructure) to the extent necessary to provide the features of the application. These third parties are contractually obligated to provide at least the same level of privacy protection as TurfAI and are prohibited from using your data for any other purpose.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">5. Data storage and retention</h2>
              <p className="text-soft-black">
                Data is temporarily stored to complete your requested workflows. We implement a retention policy that deletes or anonymizes data once the workflow is fulfilled, unless you request extended storage or we are legally required to retain it.
              </p>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">6. Security measures</h2>
              <p className="text-soft-black mb-4">
                We use the following security controls to protect your information, including:
              </p>
              <ul className="space-y-3 text-soft-black">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Encryption:</strong> Data is encrypted both in transit (TLS 1.2+) and at rest (AES-256).</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Secure Execution:</strong> Workflows run in isolated, secure environments.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Audit Logging:</strong> Monitoring of access to ensure compliance with this policy.</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">7. User control and revocation</h2>
              <ul className="space-y-3 text-soft-black">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Revoke Access:</strong> You may revoke TurfAI's access to your Google Account at any time via the <a href="https://myaccount.google.com/permissions" className="prose-link">Google Security Settings</a>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-royal-indigo rounded-full mt-2 flex-shrink-0"></div>
                  <span><strong>Data Deletion:</strong> You may request the deletion of your account and associated data by contacting <a href={`mailto:${siteConfig.contact.general}`} className="prose-link">{siteConfig.contact.general}</a>.</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-[24px] lg:text-[28px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">8. Contact us</h2>
              <p className="text-soft-black">
                For any questions regarding this Privacy Policy or our data practices, please contact:
              </p>
              <p className="text-soft-black mt-2">
                Email: <a href={`mailto:${siteConfig.contact.general}`} className="prose-link">{siteConfig.contact.general}</a>
              </p>
              <p className="text-soft-black">
                Website: <a href="https://turfai.in" className="prose-link">https://turfai.in</a>
              </p>
            </div>

            <Separator className="my-8" />

            <div className="text-center">
              <p className="text-sm text-soft-black mb-4">
                Questions about this privacy policy?
              </p>
              <p className="text-sm text-soft-black">
                Contact us at <a href={`mailto:${siteConfig.contact.general}`} className="prose-link">{siteConfig.contact.general}</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
